import { AxiosError } from "axios";

import { getAccessToken, getCustomerId } from "@/helpers/auth-helpers";
import {
  ICustomerPlatform,
  ICustomerPlatformInput,
} from "@/interfaces/customer-platform.interfaces";
import { apiMeta, apiT2B } from "@/lib/axios";

const baseCustomerPlatformUrl = "api/v1/customerplatform";
const mediaSaveFromUrl = "api/v1/midia/savefromurl";

export async function updateWhatsappGeneralInfo(
  whatsPlatform: ICustomerPlatform
): Promise<ICustomerPlatform> {
  try {
    // Get user tokens (similar to headers in Next.js)
    const userToken = getAccessToken();
    const customerId = getCustomerId();

    if (!userToken || !customerId) {
      throw new Error("Authentication required");
    }

    // Buscar o objeto completo da API para preservar todos os campos
    // Isso evita que campos como configuration, accessToken, etc. sejam perdidos
    let fullPlatform: ICustomerPlatform;
    try {
      const response = await apiT2B.get(`${baseCustomerPlatformUrl}/${whatsPlatform.id}`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          CustomerId: customerId,
        },
      });
      fullPlatform = response.data;
    } catch (error) {
      // Se não conseguir buscar, usar o objeto passado como fallback
      fullPlatform = whatsPlatform;
    }

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
    }

    const waba_id = fullPlatform.customerExternalId;
    const phone_number_id = fullPlatform.platformProfileId;

    if (!waba_id || !phone_number_id) {
      throw new Error("Missing WhatsApp Business Account ID or Phone Number ID");
    }

    let display_phone_number: string | undefined;
    let verified_name: string | undefined;
    let publicUrl: string | undefined;

    const metaToken = import.meta.env.VITE_META_T2B_ACCESS_TOKEN as string | undefined;
    const needsEnrichment =
      !fullPlatform.displayNumber || !fullPlatform.displayName || !fullPlatform.accountImage;

    // Try to get data from Meta API only if needed and token is present, otherwise skip
    if (needsEnrichment && metaToken && metaToken.trim() !== "") {
      try {
        // Get display_phone_number and verified_name from Meta API
        const phoneNumbersResponse = await apiMeta.get(`${waba_id}/phone_numbers`);
        const phoneData = phoneNumbersResponse?.data?.data?.[0];

        if (phoneData) {
          display_phone_number = phoneData.display_phone_number;
          verified_name = phoneData.verified_name;

          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
          }
        }

        // Get profile_picture_url from Meta API
        const profilePictureResponse = await apiMeta.get(
          `${phone_number_id}/whatsapp_business_profile?fields=profile_picture_url`
        );
        const profileData = profilePictureResponse?.data?.data?.[0];
        const { profile_picture_url } = profileData || {};

        // Save profile picture if it exists
        if (profile_picture_url) {
          try {
            const mediaSaveResponse = await apiT2B.post(
              mediaSaveFromUrl,
              {
                url: profile_picture_url,
              },
              {
                headers: {
                  Authorization: `Bearer ${userToken}`,
                  CustomerId: customerId,
                },
              }
            );
            publicUrl = mediaSaveResponse.data?.publicUrl;

            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
            }
          } catch (error) {
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
            }
          }
        }
      } catch (metaError) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
        }
        // Continue without Meta data - this is not a blocking error
      }
    } else if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
    }

    // Construir objeto de atualização preservando TODOS os campos existentes
    // IMPORTANTE: Active deve ser sempre preservado do objeto completo do GET
    // Se o GET retornou Active, usar esse valor; senão, usar o valor original
    const activeValue =
      fullPlatform.Active !== undefined && fullPlatform.Active !== null
        ? fullPlatform.Active
        : whatsPlatform.Active !== undefined && whatsPlatform.Active !== null
          ? whatsPlatform.Active
          : true; // Fallback para true se não encontrar em nenhum lugar

    const updatedFields: ICustomerPlatformInput = {
      // Campos obrigatórios - sempre incluídos
      platformId: fullPlatform.platformId,
      customerExternalId: fullPlatform.customerExternalId || "",
      platformProfileId: fullPlatform.platformProfileId || "",
      Active: activeValue, // ✅ Preservar valor existente do GET (true ou false)

      // Campos opcionais - incluir apenas se existirem (não undefined/null)
      ...(fullPlatform.configuration !== undefined &&
        fullPlatform.configuration !== null && {
          configuration: fullPlatform.configuration,
        }),
      ...(fullPlatform.accessToken !== undefined &&
        fullPlatform.accessToken !== null && {
          accessToken: fullPlatform.accessToken,
        }),
      ...(fullPlatform.recoveryCode !== undefined &&
        fullPlatform.recoveryCode !== null && {
          recoveryCode: fullPlatform.recoveryCode,
        }),
      ...(fullPlatform.tokenExpiresAt !== undefined &&
        fullPlatform.tokenExpiresAt !== null && {
          tokenExpiresAt: fullPlatform.tokenExpiresAt,
        }),
      ...(fullPlatform.refreshToken !== undefined &&
        fullPlatform.refreshToken !== null && {
          refreshToken: fullPlatform.refreshToken,
        }),
      ...(fullPlatform.PlatformAccountInfo !== undefined &&
        fullPlatform.PlatformAccountInfo !== null && {
          PlatformAccountInfo: fullPlatform.PlatformAccountInfo,
        }),
      ...(fullPlatform.customerId !== undefined &&
        fullPlatform.customerId !== null && {
          customerId: fullPlatform.customerId,
        }),

    // Campos de exibição - usar valores novos se disponíveis, senão preservar existentes
    displayName: verified_name || fullPlatform.displayName || undefined,
    displayNumber: display_phone_number || fullPlatform.displayNumber || undefined,
    accountImage: publicUrl || fullPlatform.accountImage || undefined,
  };

    // Verificar se há mudanças nos campos essenciais antes de fazer PUT
    // Considera mudança se: novo valor existe E é diferente do atual OU campo atual está vazio e temos novo valor
    const hasDisplayNameChange =
      verified_name &&
      (verified_name !== fullPlatform.displayName || !fullPlatform.displayName);
    const hasDisplayNumberChange =
      display_phone_number &&
      (display_phone_number !== fullPlatform.displayNumber || !fullPlatform.displayNumber);
    const hasAccountImageChange =
      publicUrl && (publicUrl !== fullPlatform.accountImage || !fullPlatform.accountImage);

  // Só fazer PUT se houver mudanças nos campos essenciais
  if (hasDisplayNameChange || hasDisplayNumberChange || hasAccountImageChange) {
    // Remover campos undefined do objeto final (para não enviar no JSON)
    const cleanFields = Object.fromEntries(
      Object.entries(updatedFields).filter(([_, value]) => value !== undefined)
    ) as ICustomerPlatformInput;

    // Update the customer platform via API
    await apiT2B.put(`${baseCustomerPlatformUrl}/${whatsPlatform.id}`, cleanFields, {
      headers: {
        Authorization: `Bearer ${userToken}`,
        CustomerId: customerId,
      },
    });

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
    }
  }

  // Retornar objeto atualizado (com ou sem PUT)
  return {
    ...fullPlatform,
    displayName: updatedFields.displayName || fullPlatform.displayName,
    displayNumber: updatedFields.displayNumber || fullPlatform.displayNumber,
    accountImage: updatedFields.accountImage || fullPlatform.accountImage,
  };
  } catch (error) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
    }

    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || "Failed to update WhatsApp general info");
    }

    throw new Error("Failed to update WhatsApp general info");
  }
}
